import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoadError } from '@/components/ui/load-state';
import { cn } from '@/lib/utils';

/** Bordered table frame: optional toolbar on top, pager (or any footer) below. */
export function DataTable({
  toolbar,
  footer,
  children,
  className,
}: {
  toolbar?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('overflow-hidden rounded-xl border border-border bg-card', className)}>
      {toolbar ? <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">{toolbar}</div> : null}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">{children}</table>
      </div>
      {footer}
    </div>
  );
}

export function DataTableHead({ columns }: { columns: Array<{ label: string; className?: string }> }) {
  return (
    <thead className="border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground">
      <tr>
        {columns.map((column) => (
          <th key={column.label} scope="col" className={cn('px-4 py-2.5 font-medium', column.className)}>
            {column.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** A row that opens `to` on click; the first cell should also carry a real link for keyboard users. */
export function DataTableRow({
  to,
  children,
  className,
}: {
  to?: string;
  children: ReactNode;
  className?: string;
}) {
  const navigate = useNavigate();
  return (
    <tr
      onClick={
        to
          ? (event) => {
              if ((event.target as HTMLElement).closest('a, button, input, [role="button"]')) return;
              navigate(to);
            }
          : undefined
      }
      className={cn(
        'border-b border-border last:border-0',
        to ? 'cursor-pointer hover:bg-muted/40' : undefined,
        className,
      )}
    >
      {children}
    </tr>
  );
}

export function DataTableCell({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 align-middle', className)}>{children}</td>;
}

/** Loading, error and empty states, spanning the whole table body. */
export function DataTableMessage({
  colSpan,
  loading,
  children,
}: {
  colSpan: number;
  loading?: boolean;
  children: ReactNode;
}) {
  return (
    <tbody>
      <tr>
        <td colSpan={colSpan} className="px-4 py-12 text-center text-sm text-muted-foreground">
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {children}
            </span>
          ) : (
            children
          )}
        </td>
      </tr>
    </tbody>
  );
}

/** A failed load spanning the whole table body, with a Retry that re-runs the fetch. */
export function DataTableLoadError({
  colSpan,
  message,
  onRetry,
}: {
  colSpan: number;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <tbody>
      <tr>
        <td colSpan={colSpan} className="px-4 py-4">
          <LoadError message={message} onRetry={onRetry} />
        </td>
      </tr>
    </tbody>
  );
}
